import React, { useState, useEffect, useRef, CSSProperties, ChangeEventHandler } from "react";

import "./customTimeSelectionButtonBar.css";

import MasterButton, { ButtonLayoutType, ButtonSize, ButtonVariation } from "../../components/button/MasterButton";
import ButtonGroup from "../buttonGroup/buttonGroup";

import InputfieldWithPlaceholder, {InputfieldType} from "../inputfieldwithplaceholder/Inputfieldwithplaceholder";

import { setHours, setMinutes } from "date-fns";
import { DayPicker, DateRange } from "react-day-picker";
import "react-day-picker/style.css";

import moment from "moment-timezone";

import CheckBox from "../../assets/CheckBox.svg"

type customTimeSelectionButtonBarProps = {
    onSelect: (data: DateRange, timezoneString: string, noOfDaysSelected?: number) => void;
    modalSize: CSSProperties;
    parentID: string;
    showCustom?: boolean;
    isMobile?:boolean;
    defaultTime?: string;
};

const formatDateToMMDDYYYY = (date: Date): string => {
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
  
    return `${month}/${day}/${year}`;
};

// Helper function to apply time and convert to specified timezone
const applyTimeAndOffset = (date: Date | undefined, time: string, offset: number): Date | undefined => {
    if (!date) return undefined;

    // Parse hours and minutes from the time string
    const [hours, minutes] = time.split(":").map(Number);

    // Create a new UTC-based date object
    const utcDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), hours, minutes));

    // Adjust the UTC date to the desired timezone offset
    const timezoneDate = new Date(utcDate.getTime() + offset * 60 * 60 * 1000);

    return timezoneDate;
};


const CustomTimeSelectionButtonBar: React.FC<customTimeSelectionButtonBarProps> = ({ onSelect, modalSize, parentID, showCustom = true, isMobile = false, defaultTime = "1 Week" }) => {
      
    const [currentTimeline, setCurrentTimeline] = useState<string>(defaultTime);
    const [innerCustomTimeline, setInnerCustomTimeline] = useState("");

    const [isLoading, setLoading] = useState(false);  // You need to get this from props

    const [isModalOpen, setIsModalOpen] = useState(false);

    const [customStartDate, setCustomStartDate] = useState("");
    const [customEndDate, setCustomEndDate] = useState("");

    const [isSearching, setIsSearching] = useState<boolean>(false); // This is the state that is used to get the state of timezone selector - whether it is currently in focus by the user or not

    // ------------------------- States used for Custom Date Range Picker

    const [range, setRange] = useState<DateRange | undefined>({
        from: undefined,
        to: new Date(),
    });
    const [leftMonth, setLeftMonth] = useState<Date>(new Date(new Date().setMonth(new Date().getMonth() - 1)));  // Left calendar month
    const [rightMonth, setRightMonth] = useState<Date>(new Date()); // Right calendar month

    // Function to handle date clicks and manage the range
    const handleDayClick = (day: Date) => {
        if (!range?.from || range.to) {
        // Start a new range if no range exists or the range is already complete
            setRange({ from: day, to: undefined });
            setCustomStartDate(formatDateToMMDDYYYY(day));
        } else {
        // Update the range with the second date
            setRange((prevRange) => {
                if (!prevRange || !prevRange.from) {
                // If there is no previous range or no 'from' date, start a new range
                    setCustomStartDate(formatDateToMMDDYYYY(day));
                    return { from: day, to: undefined };
                }
            
                // If the clicked day is earlier than 'from', set it as the new 'from'
                // return day < prevRange.from
                // ? { from: day, to: prevRange.from }
                // : { from: prevRange.from, to: day }; // Otherwise, set it as 'to'

                if (day < prevRange.from){
                    setCustomStartDate(formatDateToMMDDYYYY(day));
                    setCustomEndDate(formatDateToMMDDYYYY(prevRange.from));
                    return { from: day, to: prevRange.from }
                }else {
                    setCustomStartDate(formatDateToMMDDYYYY(prevRange.from));
                    setCustomEndDate(formatDateToMMDDYYYY(day));
                    return { from: prevRange.from, to: day }
                }

            });
        }


    };

    // ------------------------------


    // ModalStyle is explicitly typed
    const [modalStyle, setModalStyle] = useState<{ top: number; left: number }>({
        top: 0,
        left: 0,
    });

    const handleCustomButtonClick = () => {
        // Toggle modal visibility
        setIsModalOpen(!isModalOpen);
    };
      

    useEffect(() => {

        // Find the parent modal container
        const parentModal = document.querySelector("." + parentID); // Replace with your modal container selector
      
        if (parentModal) {
            // Get the parent modal's bounding rectangle
            const parentRect = parentModal.getBoundingClientRect();
        
            // Calculate modal position relative to the parent modal
            const modalPosition = {
                top: parentRect.y - 15, // Modal below the button relative to the parent modal
                left: parentRect.right - parentRect.left - 270, // Adjust for modal width relative to the parent modal
            };
            
            console.log(parentModal);
            console.log(parentRect);
            console.log(modalPosition.top);
            console.log(modalPosition.left);
        
            setModalStyle(modalPosition);
        }

    }, [])
      

    // ----------------------------------------------------------------------------------------------------------------------------------
    // ---------- Below is the function that handles the selection of Time Range in this component and sends it to the parent component 

    // Function to calculate relative datetime
    function getRelativeDate(date: Date, adjustment: { days?: number; months?: number; years?: number }): Date {
        const newDate = new Date(date); // Create a copy of the date object
    
        if (adjustment.days) {
        newDate.setUTCDate(newDate.getUTCDate() + adjustment.days);
        }
        if (adjustment.months) {
        newDate.setUTCMonth(newDate.getUTCMonth() + adjustment.months);
        }
        if (adjustment.years) {
        newDate.setUTCFullYear(newDate.getUTCFullYear() + adjustment.years);
        }
    
        return newDate;
    }


    // Function to handle clicks outside the modal
    const handleFinalDateTimeSelection = (timeline: string) => {

        const now = new Date();

        const currentDate = new Date(Date.UTC(
            now.getUTCFullYear(),
            now.getUTCMonth(),
            now.getUTCDate(),
            now.getUTCHours(),
            now.getUTCMinutes(),
            now.getUTCSeconds(),
            now.getUTCMilliseconds()
        ));

        if (timeline === "Today"){
            setCurrentTimeline("Today")

            // Send DateRange to Parent
            onSelect({
                from: getRelativeDate(currentDate, { days: -1 }),
                to: currentDate,
            }, selectedTimezoneString, 1)

        } else if (timeline === "3 Days"){
            setCurrentTimeline("3 Days")

            // Send DateRange to Parent
            onSelect({
                from: getRelativeDate(currentDate, { days: -3 }),
                to: currentDate,
            }, selectedTimezoneString, 3)

        }else if (timeline === "1 Week"){
            setCurrentTimeline("1 Week")

            // Send DateRange to Parent
            onSelect({
                from: getRelativeDate(currentDate, { days: -7 }),
                to: currentDate,
            }, selectedTimezoneString, 7)

        } else if (timeline === "1 Month"){
            setCurrentTimeline("1 Month")

            // Send DateRange to Parent
            onSelect({
                from: getRelativeDate(currentDate, { months: -1 }),
                to: currentDate,
            }, selectedTimezoneString)

        } else if (timeline === "Last 14 Days") {
            setCurrentTimeline("Custom") // Since this is inside of Custom Button in ButtonBar
            setInnerCustomTimeline("Last 14 Days")

            // Send DateRange to Parent
            onSelect({
                from: getRelativeDate(currentDate, { days: -14 }),
                to: currentDate,
            }, selectedTimezoneString)
            
        } else if (timeline === "Last 3 Months") {
            setCurrentTimeline("Custom") // Since this is inside of Custom Button in ButtonBar
            setInnerCustomTimeline("Last 3 Months")

            // Send DateRange to Parent
            onSelect({
                from: getRelativeDate(currentDate, { months: -3 }),
                to: currentDate,
            }, selectedTimezoneString)
            
        } else if (timeline === "Last 6 Months") {
            setCurrentTimeline("Custom") // Since this is inside of Custom Button in ButtonBar
            setInnerCustomTimeline("Last 6 Months")

            // Send DateRange to Parent
            onSelect({
                from: getRelativeDate(currentDate, { months: -6 }),
                to: currentDate,
            }, selectedTimezoneString)

            
        } else if (timeline === "Last Year") {
            setCurrentTimeline("Custom") // Since this is inside of Custom Button in ButtonBar
            setInnerCustomTimeline("Last Year")
            
            // Send DateRange to Parent
            onSelect({
                from: getRelativeDate(currentDate, { years: -1 }),
                to: currentDate,
            }, selectedTimezoneString)

            
        } else if (timeline === "Custom") {
            setCurrentTimeline("Custom")
            setInnerCustomTimeline("")

            
            const updatedFrom = applyTimeAndOffset(range?.from, startTimeValue, selectedTimezone);
            const updatedTo = applyTimeAndOffset(range?.to, endTimeValue, selectedTimezone);

            console.log("Updated From:", updatedFrom);
            console.log("Updated To:", updatedTo);

            // Send the updated values to the parent component
            onSelect({
                from: updatedFrom,
                to: updatedTo,
            }, selectedTimezoneString);
            
        }

        setIsModalOpen(false);
        if(!(timeline === "Custom")){
            setRange({ from: undefined, to: undefined });
            setCustomStartDate("")
            setCustomEndDate("")
        }
        

    };



    // --------------------------------------------------------------------------------------------------------------------

    let buttons = [
        {
          label: "Today",
          icon: "", // assuming you have an icon file or JSX element
          layoutType: ButtonLayoutType.Label,
          size: ButtonSize.Medium,
          variation: currentTimeline === "Today" ? ButtonVariation.NeutralTonal : ButtonVariation.Outlined,
          onClick: () => handleFinalDateTimeSelection("Today"),
          disabled: isLoading
        }
    ];

    if(isMobile){
        buttons = buttons.concat({
            label: "3 Days",
            icon: "", // assuming you have an icon file or JSX element
            layoutType: ButtonLayoutType.Label,
            size: ButtonSize.Medium,
            variation: currentTimeline === "3 Days" ? ButtonVariation.NeutralTonal : ButtonVariation.Outlined,
            onClick: () => handleFinalDateTimeSelection("3 Days"),
            disabled: isLoading
        })
    }

    buttons = buttons.concat({
        label: "1 Week",
        icon: "", // assuming you have an icon file or JSX element
        layoutType: ButtonLayoutType.Label,
        size: ButtonSize.Medium,
        variation: currentTimeline === "1 Week" ? ButtonVariation.NeutralTonal : ButtonVariation.Outlined,
        onClick: () => handleFinalDateTimeSelection("1 Week"),
        disabled: isLoading
    })



    if(!isMobile){
        buttons = buttons.concat({
            label: "1 Month",
            icon: "",
            layoutType: ButtonLayoutType.Label,
            size: ButtonSize.Medium,
            variation: currentTimeline === "1 Month" ? ButtonVariation.NeutralTonal : ButtonVariation.Outlined,
            onClick: () => handleFinalDateTimeSelection("1 Month"),
            disabled: isLoading
        })
    }
        
    if(showCustom){
    
        buttons = buttons.concat({
            label: "Custom",
            icon: "",
            layoutType: ButtonLayoutType.Label,
            size: ButtonSize.Medium,
            variation: currentTimeline === "Custom" ? ButtonVariation.NeutralTonal : ButtonVariation.Outlined,
            onClick: () => handleCustomButtonClick(),
            disabled: isLoading
        })
    }


    // ------------ Modal Click Outside Functionalities ----------------

    const modalRef = useRef<HTMLDivElement | null>(null);

    // Function to handle clicks outside the modal
    const handleClickOutside = (event: MouseEvent) => {
        if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
            setIsModalOpen(false); // Close the modal
        }
    };


    useEffect(() => {
        if (isModalOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        // Cleanup the event listener when modal closes or component unmounts
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isModalOpen]);

    // --------------------------------------------------------------------
    // -------------------------- Below is the state that activates the button to select the custom date range
    
    const [validTimeRange, setValidTimeRange] = useState(true); // Need to inverse the names to make it more readable....

    useEffect(() => {
        // console.log("Range Selected:", range);

        if(range){
            const { from, to } = range;
            if (from instanceof Date && !isNaN(from.getTime()) && to instanceof Date && !isNaN(to.getTime()) && from.getTime() <= to.getTime() && !isSearching) {
                setValidTimeRange(false)
            } else {
                setValidTimeRange(true)
            }
        }

    }, [range, isSearching]);

    // -----------------------------------------------------------------------
    // ------------------------ Below is the code that checks if the users has entered a valid time range in the input fields
    
    const [validCustomStartDate, setValidCustomStartDate] = useState(true)
    const [validCustomEndDate, setValidCustomEndDate] = useState(true)

    useEffect(() => {
         
        const dateRangeStart = new Date(2019, 0)
        const dateRangeEnd = new Date()
        const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;

        if (dateRegex.test(customStartDate)) {
            // Convert the input to a Date object
            const [month, day, year] = customStartDate.split("/");
            const date = new Date(`${year}-${month}-${day}`);
            if (!isNaN(date.getTime()) && date >= dateRangeStart && date <= dateRangeEnd) {
                // -- Set this date to the leftDate
                // handleDayClick(date);
                // console.log(date);
                setRange({ from: date, to: range?.to });
                // console.log("Date is valid");
                setValidCustomStartDate(true)
            } else {
                // console.log("Invalid date!");
                // Also show indicator that the date is invalid
                // Make the date undefined here
                setRange({ from: undefined, to: range?.to });
                setValidCustomStartDate(false)
            }
        } else {
            // alert("Invalid format! Please use MM/DD/YYYY.");
            // Make the date undefined here
            setRange({ from: undefined, to: range?.to });
            setValidCustomStartDate(false)
        } 

    }, [customStartDate]);

    useEffect(() => {

        const dateRangeStart = new Date(2019, 0)
        const dateRangeEnd = new Date()
        const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;

        if (dateRegex.test(customEndDate)) {
            // Convert the input to a Date object
            const [month, day, year] = customEndDate.split("/");
            const date = new Date(`${year}-${month}-${day}`);
            if (!isNaN(date.getTime()) && date >= dateRangeStart && date <= dateRangeEnd) {
                // -- Set this date to the leftDate
                // handleDayClick(date);
                setRange({ from: range?.from, to: date });
                // console.log("Date is valid");
                setValidCustomEndDate(true)
            } else {
                // console.log("Invalid date!");
                setRange({ from: range?.from, to: undefined });
                setValidCustomEndDate(false)
            }
        } else {
            // alert("Invalid format! Please use MM/DD/YYYY.");
            // Make the date undefined here
            setRange({ from: range?.from, to: undefined });
            setValidCustomEndDate(false)
        }

    }, [customEndDate]);

    // ---------------------------------------------------------------------------------------------------
    // Code and Hooks for setting time in the above Time Selection Modal

    const [startTimeValue, setStartTimeValue] = useState<string>("00:00"); // Need to change default values here based on what is sent via the API by default earlier
    const [endTimeValue, setEndTimeValue] = useState<string>("00:00");

    const handleStartTimeChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        const time = e.target.value;
        setStartTimeValue(time);
    };

    const handleEndTimeChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        const time = e.target.value;
        setEndTimeValue(time);
    };

    // const [hours, minutes] = time.split(":").map((str) => parseInt(str, 10)); // These two lines is the logic to set the time on the date.. 
    // const newSelectedDate = setHours(setMinutes(selected, minutes), hours);

    // ----------------------------------------------------------------------------------------------------
    // Code and hooks for setting timezone specific information

    // const timezone = Intl.DateTimeFormat().resolvedOptions();
    // console.log("The local timezone is:", timezone);

    const timezones = moment.tz.names().map((timezone) => {
        const offset = moment.tz(timezone).utcOffset() / 60;
        const offsetStr = `UTC${offset >= 0 ? "+" : ""}${offset}`;
        return { label: `${timezone} (${offsetStr})`, value: timezone, timezoneOffset: offset };
    });

    const [selectedTimezone, setSelectedTimezone] = useState<number>(0);
    const [selectedTimezoneString, setSelectedTimezoneString] = useState<string>("");
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);

    const filteredTimezones = searchTerm
        ? timezones.filter((tz) =>
              tz.label.toLowerCase().includes(searchTerm.toLowerCase())
          )
        : timezones;

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        setDropdownOpen(true);
        setIsSearching(true);
    };

    // Set default timezone to the browser's timezone
    useEffect(() => {
        const browserTimezone = moment.tz.guess(); // Get the browser's timezone
        const offset = moment.tz(browserTimezone).utcOffset() / 60;
        setSelectedTimezone(offset);
        setSelectedTimezoneString(browserTimezone);
        setSearchTerm(browserTimezone);
    }, []);

    const handleTimezoneSelect = (value: string, timezoneOffset: number) => {
        setSelectedTimezone(timezoneOffset);
        setSelectedTimezoneString(value);
        setSearchTerm(value);
        setDropdownOpen(false);
        setIsSearching(false);
    };

    const toggleDropdown = () => {
        setDropdownOpen(!dropdownOpen);
    };

    // If no timezone selected - default to browsers timezone

    // -------------------------------------------------------------------------------------------------------

    return (
        
        <div className={parentID}>
            
            <ButtonGroup buttons={buttons} groupClassName="custom-button-group" />

            {/* Modal */}
            {isModalOpen && !isLoading && (
                <div
                    className="custom-time-selection-modal"
                    style={{
                        top: `${modalStyle.top}px`,
                        right: `${modalStyle.left}px`,
                    }}
                    ref={modalRef}
                >

                    {/* <div className="custom-time-selection-modal-header">



                    </div> */}

                    <div className="scrollable-time-selection-elements" style={modalSize}>
                        
                        {/* <span className="title label-medium">Quick Selections:</span>
                        <span className="title label-medium"></span> */}
                        <div className="last-dates">
                            <MasterButton
                                options={{
                                layoutType: ButtonLayoutType.Label,
                                label: "Last 14 Days",
                                variation: innerCustomTimeline === "Last 14 Days" ? ButtonVariation.PrimaryFilled : ButtonVariation.Outlined,
                                icon: "",
                                size: ButtonSize.Medium,
                                }}
                                onClick={() => {
                                    handleFinalDateTimeSelection("Last 14 Days")
                                // reloadData()
                                // loadData(selectedStudy); // !!!!!!!!!!!!! Need to use the studyInfo state here to get the relevant study and load the data through the API
                                }}
                            ></MasterButton>

                            <MasterButton
                                options={{
                                layoutType: ButtonLayoutType.Label,
                                label: "Last 3 Months",
                                variation: innerCustomTimeline === "Last 3 Months" ? ButtonVariation.PrimaryFilled : ButtonVariation.Outlined,
                                icon: "",
                                size: ButtonSize.Medium,
                                }}
                                onClick={() => {
                                    handleFinalDateTimeSelection("Last 3 Months")
                                // reloadData()
                                // loadData(selectedStudy); // !!!!!!!!!!!!! Need to use the studyInfo state here to get the relevant study and load the data through the API
                                }}
                            ></MasterButton>

                            <MasterButton
                                options={{
                                layoutType: ButtonLayoutType.Label,
                                label: "Last 6 Months",
                                variation: innerCustomTimeline === "Last 6 Months" ? ButtonVariation.PrimaryFilled : ButtonVariation.Outlined,
                                icon: "",
                                size: ButtonSize.Medium,
                                }}
                                onClick={() => {
                                    handleFinalDateTimeSelection("Last 6 Months")
                                // reloadData()
                                // loadData(selectedStudy); // !!!!!!!!!!!!! Need to use the studyInfo state here to get the relevant study and load the data through the API
                                }}
                            ></MasterButton>

                            <MasterButton
                                options={{
                                layoutType: ButtonLayoutType.Label,
                                label: "Last Year",
                                variation: innerCustomTimeline === "Last Year" ? ButtonVariation.PrimaryFilled : ButtonVariation.Outlined,
                                icon: "",
                                size: ButtonSize.Medium,
                                }}
                                onClick={() => {
                                    handleFinalDateTimeSelection("Last Year")
                                // reloadData()
                                // loadData(selectedStudy); // !!!!!!!!!!!!! Need to use the studyInfo state here to get the relevant study and load the data through the API
                                }}
                            ></MasterButton>
                        </div>

                        <div className="separator seperator-small"></div>

                        <div className="custom-time-selectors">
                            <div className="left-time-selectors">
                                <span className="title label-medium">From</span>
                                <InputfieldWithPlaceholder
                                    collapsable={false}
                                    value={customStartDate}
                                    onValueChanged={(value) => {
                                        // if (value !== "") {
                                        //     setSearchActive(true);
                                        // } else {
                                        //     setSearchActive(false);
                                        // }
                                        // setSearchQuery(value);
                                        // search(value);
                                        setCustomStartDate(value)
                                    }}
                                    type={validCustomStartDate ? InputfieldType.Normal : InputfieldType.Error}
                                    placeholderValue="MM/DD/YYYY"
                                ></InputfieldWithPlaceholder>
                                <DayPicker
                                    captionLayout="dropdown-years"
                                    fixedWeeks
                                    mode="range"
                                    showWeekNumber
                                    timeZone="UTC" 
                                    selected={range}
                                    onSelect={(selectedRange) => setRange(selectedRange)}
                                    onDayClick={(day) => handleDayClick(day)}
                                    month={leftMonth}
                                    onMonthChange={(month) => {
                                        setLeftMonth(month)
                                        setRange({
                                            from: undefined,
                                            to: undefined,
                                        })
                                        setCustomStartDate("")
                                        setCustomEndDate("");
                                    }}
                                    disabled={{ after: new Date() }}
                                    startMonth={new Date(2019, 0)}
                                    endMonth={new Date(rightMonth.getFullYear(), rightMonth.getMonth()-1)}
                                />

                                <span className="title label-medium">Start Time:</span>
                                <form className="time-selectors">
                                    <label>
                                        <input type="time" value={startTimeValue} onChange={handleStartTimeChange} />
                                    </label>
                                </form>

                            </div>
                            <div className="right-time-selectors">
                                <span className="title label-medium">To</span>
                                <InputfieldWithPlaceholder
                                    collapsable={false}
                                    value={customEndDate}
                                    onValueChanged={(value) => {
                                        // if (value !== "") {
                                        //     setSearchActive(true);
                                        // } else {
                                        //     setSearchActive(false);
                                        // }
                                        // setSearchQuery(value);
                                        // search(value);
                                        
                                        setCustomEndDate(value)

                                    }}
                                    type={validCustomEndDate ? InputfieldType.Normal : InputfieldType.Error}
                                    placeholderValue="MM/DD/YYYY"
                                ></InputfieldWithPlaceholder>
                                <DayPicker
                                    captionLayout="dropdown-years"
                                    fixedWeeks
                                    mode="range"
                                    showWeekNumber
                                    timeZone="UTC" 
                                    selected={range}
                                    onSelect={(selectedRange) => setRange(selectedRange)}
                                    onDayClick={(day) => handleDayClick(day)}
                                    month={rightMonth}
                                    onMonthChange={(month) => {
                                        // setLeftMonth(new Date(new Date().setMonth(month.getMonth() - 1))) 
                                        if(month.getMonth() <= leftMonth.getMonth()) {
                                            setLeftMonth(new Date(month.getFullYear(), month.getMonth()-1)) 
                                        }
                                        setRightMonth(month)
                                        setRange({
                                            from: undefined,
                                            to: undefined,
                                        })
                                        setCustomStartDate("")
                                        setCustomEndDate("");
                                    }}
                                    disabled={{ after: new Date() }}
                                    startMonth={new Date(2019, 1)}
                                    endMonth={new Date(new Date().getFullYear(), new Date().getMonth())}
                                /> 

                                <span className="title label-medium">End Time:</span>
                                <form className="time-selectors">
                                    <label>
                                        <input type="time" value={endTimeValue} onChange={handleEndTimeChange} />
                                    </label>
                                </form> 

                            </div>
                        </div>

                        <div className="separator seperator-small"></div>

                        <span className="title label-medium">Time Zone:</span>
                        
                        <div className="timezone-selector">
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <input
                                    type="text"
                                    placeholder="Search or choose a timezone"
                                    value={searchTerm}
                                    onChange={handleInputChange}
                                    className="custom-input"
                                />
                                <button
                                    onClick={toggleDropdown}
                                    className="chevron-button"
                                >
                                    ▼
                                </button>
                            </div>
                            {dropdownOpen && (
                                <ul
                                    className="dropdown-menu"
                                >
                                    {filteredTimezones.length > 0 ? (
                                        filteredTimezones.map((tz) => (
                                            <li
                                                key={tz.value}
                                                onClick={() => handleTimezoneSelect(tz.value, tz.timezoneOffset)}
                                                className="dropdown-item"
                                                style={{ padding: "8px", cursor: "pointer" }}
                                            >
                                                {tz.label}
                                            </li>
                                        ))
                                    ) : (
                                        <li className="dropdown-item" style={{ padding: "8px" }}>
                                            No results found
                                        </li>
                                    )}
                                </ul>
                            )}
                        </div>
                        
                        {/* {selectedTimezone && (
                        <p>
                            Selected Timezone: <strong>{selectedTimezone}</strong>
                        </p>
                        )} */}

                        {/* <div className="separator seperator-small"></div> */}
                        
                        

                    </div>

                    <div className="custom-time-selection-modal-footer">

                        <div className="range-selection-div">
                            <MasterButton
                                options={{
                                layoutType: ButtonLayoutType.IconLabel,
                                label: "Apply Date Selection",
                                variation: ButtonVariation.PrimaryFilled,
                                icon: CheckBox,
                                size: ButtonSize.Medium,
                                }}
                                onClick={() => {
                                    handleFinalDateTimeSelection("Custom")
                                }}
                                disabled={validTimeRange}
                            ></MasterButton>
                        </div>

                    </div>

                </div>
            )}
        </div>
                
    );
};

export default CustomTimeSelectionButtonBar;

// Current Bugs in Time Selection Component
// 1. Initial Error Loading in input field when the component is first opened
// 2. if To < From Date in input field, it should show error in input field
// 3. The month should automatically move when the input field is entered.

// If the above are completed, then its a matter of refactoring the code and documenting it and getting the range of dates from this.